import { Component } from "@theme/component";
import {
	fetchConfig,
	debounce,
	onAnimationEnd,
	prefersReducedMotion,
	resetShimmer,
	syncPaymentRestrictedAttribute,
} from "@theme/utilities";
import { morphSection, sectionRenderer } from "@theme/section-renderer";
import {
	ThemeEvents,
	CartUpdateEvent,
	QuantitySelectorUpdateEvent,
	CartAddEvent,
	DiscountUpdateEvent,
} from "@theme/events";
import { cartPerformance } from "@theme/performance";

/** @typedef {import('./utilities').TextComponent} TextComponent */

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
	#debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);

	connectedCallback() {
		super.connectedCallback();

		document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
		document.addEventListener(
			ThemeEvents.discountUpdate,
			this.handleDiscountUpdate,
		);
		document.addEventListener(
			ThemeEvents.quantitySelectorUpdate,
			this.#debouncedOnChange,
		);
	}

	disconnectedCallback() {
		super.disconnectedCallback();

		document.removeEventListener(
			ThemeEvents.cartUpdate,
			this.#handleCartUpdate,
		);
		document.removeEventListener(
			ThemeEvents.quantitySelectorUpdate,
			this.#debouncedOnChange,
		);
	}

	/**
	 * Handles QuantitySelectorUpdateEvent change event.
	 * @param {QuantitySelectorUpdateEvent} event - The event.
	 */
	#onQuantityChange(event) {
		const { quantity, cartLine: line, key } = event.detail;

		if (!line && !key) return;

		/** @type {HTMLElement | null} */
		let row = null;

		if (key) {
			row = this.querySelector(`tr[data-key="${key}"]`);
		}

		if (!row && line) {
			row = this.querySelector(`tr[data-index="${line}"]`);
		}

		/** @type {Record<string, number>} */
		const updates = {};

		// If the row is valid and we have a key (from row or event), use it
		const rowKey = row instanceof HTMLElement ? row.dataset.key : key;

		if (rowKey) {
			updates[rowKey] = quantity;

			// If it's a main product, find any addons and update their quantity too
			if (
				row instanceof HTMLElement &&
				row.dataset.isMain === "true" &&
				row.dataset.groupId
			) {
				const groupId = row.dataset.groupId;
				const addons = this.querySelectorAll(
					`tr[data-is-addon="true"][data-group-id="${groupId}"]`,
				);

				for (const addon of addons) {
					if (addon instanceof HTMLElement && addon.dataset.key) {
						updates[addon.dataset.key] = quantity;
					}
				}
			}

			if (quantity === 0) {
				return this.onLineItemRemove(line || rowKey || 0);
			}

			this.updateQuantities({
				updates,
				action: "change",
				line: line ? Number(line) : 0,
			});
		} else {
			// Fallback to original logic if no key
			if (quantity === 0) {
				return this.onLineItemRemove(line);
			}

			this.updateQuantity({
				line,
				quantity,
				action: "change",
			});
		}

		// Shimmer effect
		if (row instanceof HTMLElement) {
			const textComponent = /** @type {TextComponent | undefined} */ (
				row.querySelector("text-component")
			);
			textComponent?.shimmer();
		}
	}

	/**
	 * Handles the line item removal.
	 * @param {number} line - The line item index.
	 */
	/**
	 * Handles the line item removal.
	 * @param {number|string} lineOrKey - The line item index or key.
	 */
	onLineItemRemove(lineOrKey) {
		let rowToRem = this.querySelector(`tr[data-index="${lineOrKey}"]`);
		if (!rowToRem) {
			rowToRem = this.querySelector(`tr[data-key="${lineOrKey}"]`);
		}

		/** @type {Record<string, number>} */
		const updates = {};

		// If we found a row by index or key
		if (rowToRem instanceof HTMLElement && rowToRem.dataset.key) {
			updates[rowToRem.dataset.key] = 0;

			// Remove addons if main
			if (rowToRem.dataset.isMain === "true" && rowToRem.dataset.groupId) {
				const groupId = rowToRem.dataset.groupId;
				const addons = this.querySelectorAll(
					`tr[data-is-addon="true"][data-group-id="${groupId}"]`,
				);
				for (const addon of addons) {
					if (addon instanceof HTMLElement && addon.dataset.key) {
						updates[addon.dataset.key] = 0;
					}
				}
			}

			this.updateQuantities({
				updates,
				action: "clear",
			});
		} else {
			// Fallback - assume lineOrKey is an index if we couldn't find a row or key
			this.updateQuantity({
				line: Number(lineOrKey),
				quantity: 0,
				action: "clear",
			});
		}

		// Animation logic
		if (!rowToRem) {
			// Try to find it in refs by index if we passed an index
			const index = Number(lineOrKey);
			if (!Number.isNaN(index)) {
				// @ts-ignore
				rowToRem = this.refs.cartItemRows[index - 1];
			}
		}

		if (!rowToRem || !(rowToRem instanceof HTMLElement)) return;

		/** @type {HTMLElement[]} */
		const rowsToRemove = [rowToRem];

		// Get all nested lines (parent-child relationship)
		// @ts-ignore
		const allRows = /** @type {HTMLElement[]} */ (this.refs.cartItemRows);

		if (rowToRem.dataset.key) {
			const nested = allRows.filter(
				(row) => row.dataset.parentKey === rowToRem.dataset.key,
			);
			rowsToRemove.push(...nested);
		}

		if (rowToRem.dataset.isMain === "true" && rowToRem.dataset.groupId) {
			const addons = this.querySelectorAll(
				`tr[data-is-addon="true"][data-group-id="${rowToRem.dataset.groupId}"]`,
			);
			for (const addon of addons) {
				if (addon instanceof HTMLElement && !rowsToRemove.includes(addon)) {
					rowsToRemove.push(addon);
				}
			}
		}

		// Add class to the row to trigger the animation
		rowsToRemove.forEach((row) => {
			const remove = () => row.remove();

			if (prefersReducedMotion()) return remove();

			row.style.setProperty("--row-height", `${row.clientHeight}px`);
			row.classList.add("removing");

			// Remove the row after the animation ends
			onAnimationEnd(row, remove);
		});
	}

	/**
	 * Updates the quantity.
	 * @param {Object} config - The config.
	 * @param {number} config.line - The line.
	 * @param {number} config.quantity - The quantity.
	 * @param {string} config.action - The action.
	 */
	updateQuantity(config) {
		this.updateQuantities({
			updates: { [config.line]: config.quantity },
			action: config.action,
			line: config.line, // Pass line for error handling if using line-based update (fallback)
		});
	}

	/**
	 * Updates multiple quantities.
	 * @param {Object} config - The config.
	 * @param {Object} config.updates - The updates object { key: qty } or { line: qty }.
	 * @param {string} config.action - The action.
	 * @param {number} [config.line] - Optional line context for error handling.
	 */
	updateQuantities(config) {
		const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(
			`${config.action}:user-action`,
		);

		this.#disableCartItems();

		const { updates } = config;
		const { cartTotal } = this.refs;

		const cartItemsComponents = document.querySelectorAll(
			"cart-items-component",
		);
		const sectionsToUpdate = new Set([this.sectionId]);
		cartItemsComponents.forEach((item) => {
			if (item instanceof HTMLElement && item.dataset.sectionId) {
				sectionsToUpdate.add(item.dataset.sectionId);
			}
		});

		const body = JSON.stringify({
			updates: updates,
			sections: Array.from(sectionsToUpdate).join(","),
			sections_url: window.location.pathname,
		});

		cartTotal?.shimmer();

		// Use update.js for batch supports
		// Note: update.js replaces the whole cart state for keys provided, or updates counts?
		// /cart/update.js updates the quantities of the items.
		// If using keys: { "key": 2, "key2": 3 }. This updates those specific lines.
		// If using line indices, it updates those lines.

		fetch(`${Theme.routes.cart_update_url}`, fetchConfig("json", { body }))
			.then((response) => {
				return response.text();
			})
			.then(async (responseText) => {
				const parsedResponseText = JSON.parse(responseText);

				resetShimmer(this);

				if (parsedResponseText.errors) {
					// Handle errors - simplistic error handling for batch
					// If we have 'line' context from config, use it, otherwise show generic error?
					// errors object structure varies.
					this.#handleCartError(config.line || 1, parsedResponseText);
					return;
				}

				try {
					await syncPaymentRestrictedAttribute(parsedResponseText);
				} catch (error) {
					console.error(error);
				}

				const newSectionHTML = new DOMParser().parseFromString(
					parsedResponseText.sections[this.sectionId],
					"text/html",
				);

				// Grab the new cart item count from a hidden element
				const newCartHiddenItemCount = newSectionHTML.querySelector(
					'[ref="cartItemCount"]',
				)?.textContent;
				const newCartItemCount = newCartHiddenItemCount
					? parseInt(newCartHiddenItemCount, 10)
					: 0;

				// Update data-cart-quantity for all matching variants
				this.#updateQuantitySelectors(parsedResponseText);

				this.dispatchEvent(
					new CartUpdateEvent({}, this.sectionId, {
						itemCount: newCartItemCount,
						source: "cart-items-component",
						sections: parsedResponseText.sections,
					}),
				);

				morphSection(
					this.sectionId,
					parsedResponseText.sections[this.sectionId],
				);

				this.#updateCartQuantitySelectorButtonStates();
			})
			.catch((error) => {
				console.error(error);
			})
			.finally(() => {
				this.#enableCartItems();
				cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
			});
	}

	/**
	 * Handles the discount update.
	 * @param {DiscountUpdateEvent} event - The event.
	 */
	handleDiscountUpdate = (event) => {
		this.#handleCartUpdate(event);
	};

	/**
	 * Handles the cart error.
	 * @param {number} line - The line.
	 * @param {Object} parsedResponseText - The parsed response text.
	 * @param {string} parsedResponseText.errors - The errors.
	 */
	#handleCartError = (line, parsedResponseText) => {
		const quantitySelector = this.refs.quantitySelectors[line - 1];
		const quantityInput = quantitySelector?.querySelector("input");

		if (!quantityInput) throw new Error("Quantity input not found");

		quantityInput.value = quantityInput.defaultValue;

		const cartItemError = this.refs[`cartItemError-${line}`];
		const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

		if (!(cartItemError instanceof HTMLElement))
			throw new Error("Cart item error not found");
		if (!(cartItemErrorContainer instanceof HTMLElement))
			throw new Error("Cart item error container not found");

		cartItemError.textContent = parsedResponseText.errors;
		cartItemErrorContainer.classList.remove("hidden");
	};

	/**
	 * Handles the cart update.
	 *
	 * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
	 */
	#handleCartUpdate = (event) => {
		if (event instanceof DiscountUpdateEvent) {
			sectionRenderer.renderSection(this.sectionId, { cache: false });
			return;
		}
		if (event.target === this) return;

		const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
		if (cartItemsHtml) {
			morphSection(this.sectionId, cartItemsHtml);

			// Update button states for all cart quantity selectors after morph
			this.#updateCartQuantitySelectorButtonStates();
		} else {
			sectionRenderer.renderSection(this.sectionId, { cache: false });
		}
	};

	/**
	 * Disables the cart items.
	 */
	#disableCartItems() {
		this.classList.add("cart-items-disabled");
	}

	/**
	 * Enables the cart items.
	 */
	#enableCartItems() {
		this.classList.remove("cart-items-disabled");
	}

	/**
	 * Updates quantity selectors for all matching variants in the cart.
	 * @param {Object} updatedCart - The updated cart object.
	 * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
	 */
	#updateQuantitySelectors(updatedCart) {
		if (!updatedCart.items) return;

		for (const item of updatedCart.items) {
			const variantId = item.variant_id.toString();
			const selectors = document.querySelectorAll(
				`quantity-selector-component[data-variant-id="${variantId}"], cart-quantity-selector-component[data-variant-id="${variantId}"]`,
			);

			for (const selector of selectors) {
				const input = selector.querySelector("input[data-cart-quantity]");
				if (!input) continue;

				input.setAttribute("data-cart-quantity", item.quantity.toString());

				// Update the quantity selector's internal state
				if (
					"updateCartQuantity" in selector &&
					typeof selector.updateCartQuantity === "function"
				) {
					selector.updateCartQuantity();
				}
			}
		}
	}

	/**
	 * Updates button states for all cart quantity selector components.
	 */
	#updateCartQuantitySelectorButtonStates() {
		const cartQuantitySelectors = document.querySelectorAll(
			"cart-quantity-selector-component",
		);
		for (const selector of cartQuantitySelectors) {
			if (
				"updateButtonStates" in selector &&
				typeof selector.updateButtonStates === "function"
			) {
				selector.updateButtonStates();
			}
		}
	}

	/**
	 * Gets the section id.
	 * @returns {string} The section id.
	 */
	get sectionId() {
		const { sectionId } = this.dataset;

		if (!sectionId) throw new Error("Section id missing");

		return sectionId;
	}
}

if (!customElements.get("cart-items-component")) {
	customElements.define("cart-items-component", CartItemsComponent);
}
